import { Auth0ProviderWithNavigate } from "@src/auth/auth0-provider-with-navigate";
import { DesktopBrowserLoginOverlay } from "@src/auth/desktop-browser-login-overlay";
import { IllumiLoadingScreen } from "@src/components/illumi-loading-screen";
import { MainLayout } from "@src/components/layouts/main-layout";
import { MainLayoutError } from "@src/components/layouts/main-layout-error";
import { useDesktopDeepLinkNavigation } from "@src/desktop/hooks/use-desktop-deep-link-navigation";
import { StrictMode, Suspense, lazy, useCallback } from "react";
import {
	Navigate,
	RouterProvider,
	createBrowserRouter,
	useParams,
} from "react-router-dom";
import { ROUTES } from "./common/constants";
import { BoardEventsProvider } from "./components/board/board-events-context";
import { loader } from "./components/layouts/main-layout-loader";
import { DataLoaderProviders } from "./components/layouts/providers/main-layout-providers";
// import { EmptyLayout } from "./components/layouts/empty-layout";
import { PublicShellLayout } from "./components/layouts/public-shell-layout";
import { inviteRedirectLoader } from "./pages/invite-redirect-loader";
import { rootLoader } from "./pages/root-loader";
import { whiteboardPageLoader } from "./pages/whiteboard-page-single-loader";

const WhiteboardBasePageLazy = lazy(() =>
	import("./pages/whiteboard-page-single").then((module) => ({
		default: module.WhiteboardPageSingle,
	})),
);

const WhiteboardWithoutIdPageLazy = lazy(() =>
	import("./pages/whiteboard-without-id-page").then((module) => ({
		default: module.WhiteboardWithoutIdPage,
	})),
);

const TrashPageLazy = lazy(() =>
	import("./pages/trash").then((module) => ({
		default: module.TrashPage,
	})),
);

const DraftPageLazy = lazy(() =>
	import("./pages/draft").then((module) => ({ default: module.DraftPage })),
);

const LibraryPageLazy = lazy(() =>
	import("./pages/library").then((module) => ({ default: module.LibraryPage })),
);

const AgentChatHomePageLazy = lazy(() =>
	import("./pages/agent-chat-home-page").then((module) => ({
		default: module.AgentChatHomePage,
	})),
);

const CardPageLazy = lazy(() =>
	import("./pages/card-page").then((module) => ({ default: module.CardPage })),
);

const NotFoundPageLazy = lazy(() =>
	import("./pages/not-found-page").then((module) => ({
		default: module.NotFoundPage,
	})),
);

/**
 * Dispatches root-level typed document routes (`/:cardId`), serving both
 * standalone cards (`/Card-<uuid>`) and fullscreen chats (`/Chat-<uuid>`).
 * Invalid card ids (including former `/home`) resolve to not-found in CardPage.
 */
function RootDocumentRoute() {
	const { cardId } = useParams<{ cardId: string }>();
	if (cardId?.toLowerCase().startsWith("chat-")) {
		return (
			<Suspense fallback={<IllumiLoadingScreen />}>
				<AgentChatHomePageLazy />
			</Suspense>
		);
	}
	return (
		<Suspense fallback={<IllumiLoadingScreen />}>
			<CardPageLazy />
		</Suspense>
	);
}

const ProfilePageLazy = lazy(() =>
	import("./pages/profile-page").then((module) => ({
		default: module.ProfilePage,
	})),
);

const CallbackPageLazy = lazy(() =>
	import("./pages/callback-page").then((module) => ({
		default: module.CallbackPage,
	})),
);

const CliLoginPageLazy = lazy(() =>
	import("./pages/cli-login-page").then((module) => ({
		default: module.CliLoginPage,
	})),
);

const SignupPageLazy = lazy(() =>
	import("./pages/signup-page").then((module) => ({
		default: module.SignupPage,
	})),
);

const EmailVerifiedPageLazy = lazy(() =>
	import("./pages/email-verified-page").then((module) => ({
		default: module.EmailVerifiedPage,
	})),
);

const NoWhiteboardsPageLazy = lazy(() =>
	import("./pages/no-whiteboards-page").then((module) => ({
		default: module.NoWhiteboardsPage,
	})),
);

const CheckoutReturnPageLazy = lazy(() =>
	import("./pages/checkout-return-page").then((module) => ({
		default: module.CheckoutReturnPage,
	})),
);

function AppLayout() {
	return (
		<DataLoaderProviders>
			<Auth0ProviderWithNavigate>
				<StrictMode>
					<BoardEventsProvider>
						<MainLayout />
					</BoardEventsProvider>
				</StrictMode>
			</Auth0ProviderWithNavigate>
		</DataLoaderProviders>
	);
}

export const routes = createBrowserRouter([
	{
		element: (
			<Auth0ProviderWithNavigate>
				<PublicShellLayout />
			</Auth0ProviderWithNavigate>
		),
		children: [
			{
				path: ROUTES.callback,
				element: <CallbackPageLazy />,
			},
			{
				path: ROUTES.cliLogin,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<CliLoginPageLazy />
					</Suspense>
				),
			},
			{
				path: ROUTES.signup,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<SignupPageLazy />
					</Suspense>
				),
			},
			{
				path: ROUTES.authEmailVerified,
				element: <EmailVerifiedPageLazy />,
			},
			{
				path: `${ROUTES.invite}/:code`,
				loader: inviteRedirectLoader,
				element: null,
			},
			{
				path: ROUTES.root,
				loader: rootLoader,
				element: null,
			},
		],
		ErrorBoundary: MainLayoutError,
		HydrateFallback: IllumiLoadingScreen,
	},
	{
		path: "/",
		element: <AppLayout />,
		loader: loader,
		shouldRevalidate: () => false,
		ErrorBoundary: MainLayoutError,
		HydrateFallback: IllumiLoadingScreen,
		children: [
			{
				path: ROUTES.whiteboards,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<WhiteboardWithoutIdPageLazy />
					</Suspense>
				),
			},
			{
				path: `${ROUTES.whiteboards}/:id`,
				loader: ({ params, request }) =>
					// biome-ignore lint/style/noNonNullAssertion: wouldn't be used if not defined
					whiteboardPageLoader(params.id!, request.url),
				ErrorBoundary: MainLayoutError,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<WhiteboardBasePageLazy />
					</Suspense>
				),
			},
			{
				path: ROUTES.trash,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<TrashPageLazy />
					</Suspense>
				),
			},
			{
				path: `${ROUTES.draft}/:ym?`,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<DraftPageLazy />
					</Suspense>
				),
			},
			{
				path: ROUTES.ask,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<AgentChatHomePageLazy />
					</Suspense>
				),
			},
			{
				path: ROUTES.library,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<LibraryPageLazy />
					</Suspense>
				),
			},
			{
				// Serves both /Card-<uuid> (CardPage) and /Chat-<uuid> (AgentChatHomePage)
				path: "/:cardId",
				element: <RootDocumentRoute />,
			},
			{
				path: ROUTES.profile,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<ProfilePageLazy />
					</Suspense>
				),
			},
			{
				path: ROUTES.checkoutReturn,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<CheckoutReturnPageLazy />
					</Suspense>
				),
			},
			{
				path: ROUTES.default,
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<NoWhiteboardsPageLazy />
					</Suspense>
				),
			},
			{
				path: "/whiteboard",
				element: (
					<Navigate
						to={`${ROUTES.whiteboards}${new URLSearchParams(location.search).get("id") ? `/${new URLSearchParams(location.search).get("id")}` : ""}`}
						replace
					/>
				),
			},
			{
				path: "*",
				element: (
					<Suspense fallback={<IllumiLoadingScreen />}>
						<NotFoundPageLazy />
					</Suspense>
				),
			},
		],
	},
]);

function App() {
	const navigateDeepLink = useCallback((to: string) => {
		void routes.navigate(to);
	}, []);

	useDesktopDeepLinkNavigation(navigateDeepLink);

	return (
		<>
			<DesktopBrowserLoginOverlay />
			<RouterProvider router={routes} />
		</>
	);
}

export default App;
